import React, { useState, useEffect, useCallback, useRef } from 'react';
import sanityClient from "../../client.js";

import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel, Pagination } from 'swiper';

import arrow from "../../assets/images/diagonal-arrow.png"

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import "./projects.scss";

function ProjectImage({ src, srcSet, sizes, alt }) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const img = new Image();
    if (srcSet) {
      img.srcset = srcSet;
      img.sizes = sizes;
    }
    img.src = src;
    img.onload = () => setLoaded(true);

    return () => { img.onload = null; };
  }, [src, srcSet, sizes]);

  return (
    <div className="progressive-image">
      <div className={`skeleton-image ${loaded ? 'skeleton-hidden' : ''}`} />
      <img
        ref={imgRef}
        className={`project-img ${loaded ? 'project-img--loaded' : ''}`}
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

function ProjectSkeleton() {
  return (
    <div className="projects">
      <ul>
        <li>
          <div className="title-box">
            <div className="table">
              <div className="row">
                <div className="column">
                  <div className="skeleton-line skeleton-title" />
                  <div className="skeleton-line skeleton-desc" />
                  <div className="skeleton-line skeleton-desc-short" />
                </div>
              </div>
              <div className="row">
                <div className="column">
                  <div className="skeleton-line skeleton-link" />
                </div>
              </div>
            </div>
            <div className="image-container">
              <div className="skeleton-image" />
            </div>
          </div>
        </li>
      </ul>
    </div>
  );
}

function Projects() {

  const [projectData, setprojectData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "projects"] | order(date desc){
                name,
                slug,
                date,
                description,
                association,
                url,
                "bannerImage": bannerImage.asset->url
                }`
      )
      .then((data) => {
        setprojectData(data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
      });
  }, []);

  const buildSrcSet = useCallback((url) => {
    if (!url) return undefined;
    return [480, 768, 1024, 1440].map((w) => `${url}?w=${w}&auto=format ${w}w`).join(", ");
  }, []);

  if (isLoading) {
    return (
      <div className="projects-container">
        <ProjectSkeleton />
      </div>
    );
  }

  return (
    <div className="projects-container">
      <Swiper
        modules={[Mousewheel, Pagination]}
        initialSlide={0}
        mousewheel={true}
        spaceBetween={5}
        slidesPerView={1}
        pagination={{
          el: ".snp-pagination",
          clickable: true,
          renderBullet: function (index, className) {
            return '<span class="' + className + '">' + (index + 1) + "</span>";
          },
        }}
        scrollbar={{ draggable: true }}
        loop={true}
        breakpoints={{
          320: {
            slidesPerView: 1,
            spaceBetween: 20
          },
          480: {
            slidesPerView: 1,
            spaceBetween: 20
          },
          640: {
            slidesPerView: 1,
            spaceBetween: 50
          }
        }
        }
      >
        {projectData &&
          projectData.map((project, idx) => (
            <SwiperSlide key={project.slug?.current || idx}>
              <div className="projects">
                <ul>
                  <li>
                    <div className="title-box">
                      <div className="table">
                        <div className="row">
                          <div className="column">
                            <p><span className="title">{project.name}</span> - {project.association}</p>
                            <p className="description">{project.description}</p>
                          </div>
                        </div>

                        <div className="row">
                          <div className="column">
                            {project.url ?
                              <p style={{ paddingTop: 43 }}>
                                <a
                                  href={project.url}
                                  className='visit'
                                  target='_blank'
                                  rel="noreferrer">Visit
                                  <img src={arrow} className='arrow' width={8} height={8} alt="" />
                                </a>
                              </p>
                              :
                              <p
                                style={{ paddingTop: 35 }}
                                className='visit'>
                                Coming Soon
                              </p>
                            }
                          </div>
                        </div>
                      </div>
                      <div className='image-container'>
                        <ProjectImage
                          src={`${project.bannerImage}?auto=format`}
                          srcSet={buildSrcSet(project.bannerImage)}
                          sizes="(max-width: 600px) 330px, (max-width: 1200px) 600px, 850px"
                          alt={project.name}
                        />
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </SwiperSlide>
          ))}
        <span className="snp-pagination"></span>
      </Swiper>
    </div>
  );
}

export default Projects;